// DraggableFreezeButton.js - Updated with container constraints
import React, { useState, useRef, useEffect } from 'react';
import './DraggableFreezeButton.css';

const DraggableFreezeButton = ({ onFreeze }) => {
  const [position, setPosition] = useState(() => {
    const savedPos = localStorage.getItem('freezeButtonPosition');
    return savedPos ? JSON.parse(savedPos) : { x: 100, y: 100 };
  });
  const [isDragging, setIsDragging] = useState(false);
  const [showSaved, setShowSaved] = useState(false);
  const buttonRef = useRef(null);
  const startPositionRef = useRef({ x: 0, y: 0 });
  const startMouseRef = useRef({ x: 0, y: 0 });
  const dragThresholdRef = useRef(false);

  // Save position to localStorage when it changes
  useEffect(() => {
    if (!isDragging) {
      localStorage.setItem('freezeButtonPosition', JSON.stringify(position));
    }
  }, [position, isDragging]);

const constrainPosition = (newX, newY) => {
  // Try to find the actual container the button is in
  const container = buttonRef.current?.closest('.stickies-display-area') || 
                   buttonRef.current?.closest('.content-container');
  
  if (!container) return { x: newX, y: newY };

  const containerRect = container.getBoundingClientRect();
  const buttonSize = 40;
  
  const constrainedX = Math.max(0, Math.min(newX, containerRect.width - buttonSize));
  const constrainedY = Math.max(0, Math.min(newY, containerRect.height - buttonSize));
  
  return { x: constrainedX, y: constrainedY };
};

  const handleMouseDown = (e) => {
    e.preventDefault();
    e.stopPropagation();
    
    startPositionRef.current = { ...position };
    startMouseRef.current = { x: e.clientX, y: e.clientY };
    
    setIsDragging(true);
  };

  const handleMouseMove = (e) => {
    if (!isDragging) return;
    
    const deltaX = e.clientX - startMouseRef.current.x;
    const deltaY = e.clientY - startMouseRef.current.y;
    
    if (Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3) {
      dragThresholdRef.current = true;
    }
    
    const newX = startPositionRef.current.x + deltaX;
    const newY = startPositionRef.current.y + deltaY;
    
    // Apply constraints
    const constrainedPos = constrainPosition(newX, newY);
    setPosition(constrainedPos);
  };

  const handleMouseUp = () => {
    setIsDragging(false);
    setTimeout(() => {
      dragThresholdRef.current = false;
    }, 100);
  };

  useEffect(() => {
    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
    } else {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    }
    
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [isDragging]);

  // Constrain position on mount and container resize
  useEffect(() => {
    const constrainedPos = constrainPosition(position.x, position.y);
    if (constrainedPos.x !== position.x || constrainedPos.y !== position.y) {
      setPosition(constrainedPos);
    }
  }, []);

  const handleClick = (e) => {
    if (isDragging || dragThresholdRef.current) return;
    
    e.stopPropagation();
    onFreeze();
    
    setShowSaved(true);
    setTimeout(() => {
      setShowSaved(false);
    }, 1000);
  };

  return (
    <div
      ref={buttonRef}
      className={`draggable-freeze-button ${isDragging ? 'dragging' : ''} ${showSaved ? 'saved' : ''}`}
      style={{
        left: `${position.x}px`,
        top: `${position.y}px`,
        cursor: isDragging ? 'grabbing' : 'grab'
      }}
      onMouseDown={handleMouseDown}
      onClick={handleClick}
      title="Freeze sticky positions (save layout)"
    >
      {showSaved ? '✓' : '❄️'}
    </div>
  );
};

export default DraggableFreezeButton;